import { Angle, degrees, radians, TWO_PI } from './Angle';
import { point } from './constructors';
import { DISTANCE_EPSILON } from './GeomConstants';
import { Point } from './Point';
import { composeSvgPathData, tokenizeSvgPathData } from './svgUtils';

/**
 * Represents an ellipse parameterized by its center, two axis vectors, and start and end angles.
 *
 * This class provides methods to convert from an ellipse defined by its endpoints and radii
 * (EndpointsEllipse) to an ellipse defined by its center and axes (CenterEllipse).
 *
 * The resulting CenterEllipse instance can be used for further geometric computations or rendering.
 */
export interface CenterEllipse {
  center: Point;
  aAxis: Point;
  bAxis: Point;
  startAngle: Angle;
  endAngle: Angle;
}

export function fromEndpointsEllipse(endpointsEllipse: EndpointsEllipse): CenterEllipse {
  // Extract parameters
  const x1 = endpointsEllipse.start.x;
  const y1 = endpointsEllipse.start.y;
  const x2 = endpointsEllipse.end.x;
  const y2 = endpointsEllipse.end.y;
  let rx = Math.abs(endpointsEllipse.radii.x);
  let ry = Math.abs(endpointsEllipse.radii.y);
  const phi = endpointsEllipse.xRotation.radians;
  const largeArc = endpointsEllipse.largeArc;
  const sweep = endpointsEllipse.sweep;

  // Handle zero radii
  if (rx < DISTANCE_EPSILON || ry < DISTANCE_EPSILON) {
    // Treat as a straight line
    const center = point((x1 + x2) / 2, (y1 + y2) / 2);
    return {
      center,
      aAxis: Point.ZERO,
      bAxis: Point.ZERO,
      startAngle: Angle.ZERO,
      endAngle: Angle.ZERO
    };
  }

  const sinPhi = Math.sin(phi);
  const cosPhi = Math.cos(phi);

  const dx = (x1 - x2) / 2;
  const dy = (y1 - y2) / 2;

  const x1p = cosPhi * dx + sinPhi * dy;
  const y1p = -sinPhi * dx + cosPhi * dy;

  const isSamePoint =
    Math.abs(x1p) < DISTANCE_EPSILON && Math.abs(y1p) < DISTANCE_EPSILON;
  if (isSamePoint) {
    // Special case for full-circle arcs
    const center = point(x1, y1);
    const aAxis = point(cosPhi * rx, sinPhi * rx);
    const bAxis = point(-sinPhi * ry, cosPhi * ry);
    return {
      center,
      aAxis,
      bAxis,
      startAngle: Angle.ZERO,
      endAngle: radians(TWO_PI)
    };
  }

  // Step 2: Correct radii if necessary
  const x1pSq = x1p * x1p;
  const y1pSq = y1p * y1p;

  const rxSq = rx * rx;
  const rySq = ry * ry;

  const lambda = x1pSq / rxSq + y1pSq / rySq;
  if (lambda > 1) {
    const scale = Math.sqrt(lambda);
    rx *= scale;
    ry *= scale;
  }

  // Recompute squared radii after scaling
  const rxSqNew = rx * rx;
  const rySqNew = ry * ry;

  // Step 3: Compute (cx', cy')
  const sign = largeArc !== sweep ? 1 : -1;
  const numerator = rxSqNew * rySqNew - rxSqNew * y1pSq - rySqNew * x1pSq;
  const denominator = rxSqNew * y1pSq + rySqNew * x1pSq;

  // Prevent division by zero and sqrt of negative number
  let coef = 0;
  if (denominator !== 0) {
    coef = sign * Math.sqrt(Math.max(0, numerator / denominator));
  }

  const cxp = coef * ((rx * y1p) / ry);
  const cyp = coef * ((-ry * x1p) / rx);

  // Step 4: Compute (cx, cy)
  const xm = (x1 + x2) / 2;
  const ym = (y1 + y2) / 2;

  const cx = cosPhi * cxp - sinPhi * cyp + xm;
  const cy = sinPhi * cxp + cosPhi * cyp + ym;

  // Step 5: Compute θ1 and Δθ
  const ux = (x1p - cxp) / rx;
  const uy = (y1p - cyp) / ry;

  // Compute (x2', y2') for the end point
  const x2p = -x1p;
  const y2p = -y1p;

  const vx = (x2p - cxp) / rx;
  const vy = (y2p - cyp) / ry;

  // Compute θ1 and θ2
  const theta1 = Math.atan2(uy, ux);
  let theta2 = Math.atan2(vy, vx);

  // Compute Δθ
  let deltaTheta = theta2 - theta1;

  // Normalize deltaTheta to the range (-π, π]
  deltaTheta = ((deltaTheta + Math.PI) % (2 * Math.PI)) - Math.PI;

  // Adjust deltaTheta based on the sweep flag
  if (sweep && deltaTheta < 0) {
    deltaTheta += 2 * Math.PI;
  } else if (!sweep && deltaTheta > 0) {
    deltaTheta -= 2 * Math.PI;
  }

  // Recompute theta2
  theta2 = theta1 + deltaTheta;

  // Step 6: Create the aAxis and bAxis vectors
  const aAxis = point(cosPhi * rx, sinPhi * rx);
  const bAxis = point(-sinPhi * ry, cosPhi * ry);

  // Step 7: Return the new CenterEllipse instance
  return {
    center: point(cx, cy),
    aAxis,
    bAxis,
    startAngle: radians(theta1),
    endAngle: radians(theta1 + deltaTheta)
  };
}

/**
 * Represents an ellipse defined by its endpoints, radii, rotation, and flags.
 *
 * This class provides a method to convert an ellipse defined by its center,
 * axis vectors, and angles into an ellipse defined by its endpoints.
 *
 * @class EndpointsEllipse
 */
export interface EndpointsEllipse {
  start: Point;
  end: Point;
  radii: Point;
  xRotation: Angle;
  largeArc: boolean;
  sweep: boolean;
}

export function fromCenterEllipse(ellipse: CenterEllipse): EndpointsEllipse {
  // Extract parameters
  const { center, aAxis, bAxis, startAngle, endAngle } = ellipse;

  // Handle zero radii
  const rx = aAxis.magnitude;
  const ry = bAxis.magnitude;
  if (rx < DISTANCE_EPSILON || ry < DISTANCE_EPSILON) {
    // Treat as a straight line
    return {
      start: ellipse.center,
      end: ellipse.center,
      radii: Point.ZERO,
      xRotation: Angle.ZERO,
      largeArc: false,
      sweep: false
    };
  }

  // Start and end angles
  const theta1 = startAngle.radians;
  const theta2 = endAngle.radians;

  // Compute the start and end points directly using the axes
  const cosTheta1 = Math.cos(theta1);
  const sinTheta1 = Math.sin(theta1);
  const x1 = center.x + aAxis.x * cosTheta1 + bAxis.x * sinTheta1;
  const y1 = center.y + aAxis.y * cosTheta1 + bAxis.y * sinTheta1;

  const cosTheta2 = Math.cos(theta2);
  const sinTheta2 = Math.sin(theta2);
  const x2 = center.x + aAxis.x * cosTheta2 + bAxis.x * sinTheta2;
  const y2 = center.y + aAxis.y * cosTheta2 + bAxis.y * sinTheta2;

  // Compute the sweep flag based on the sign of the determinant
  const dx1 = x1 - center.x;
  const dy1 = y1 - center.y;
  const dx2 = x2 - center.x;
  const dy2 = y2 - center.y;
  const s = dx1 * dy2 - dx2 * dy1;
  const sweep = s >= 0;

  // Compute deltaTheta for largeArc flag
  let deltaTheta = theta2 - theta1;
  // Normalize deltaTheta to be within -2π to 2π
  deltaTheta = deltaTheta % (2 * Math.PI);
  // Compute the absolute value of deltaTheta
  const absDeltaTheta = Math.abs(deltaTheta);
  // Compute the largeArc flag (f_A)
  const largeArc = absDeltaTheta > Math.PI;

  // Compute the rotation angle phi from aAxis
  const phi = aAxis.angleWithOrigin.radians;

  // Create and return the EndpointsEllipse instance
  return {
    start: point(x1, y1),
    end: point(x2, y2),
    radii: point(rx, ry),
    xRotation: radians(phi),
    largeArc,
    sweep
  };
}

/**
 * Converts the ellipse parameters to an SVG path data string.
 *
 * @param {EndpointsEllipse} ellipse - The ellipse parameters to convert.
 * @param {number} [fractionDigits] - The number of decimal places to include in the output. If not provided, the default precision will be used.
 * @returns {string} The SVG path data string representing the ellipse as an arc.
 */
export function svgPathDataFromEndpointsEllipse(
  ellipse: EndpointsEllipse,
  fractionDigits?: number,
  includeMove = true
): string {
  const startX = ellipse.start.x;
  const startY = ellipse.start.y;
  const rx = ellipse.radii.x;
  const ry = ellipse.radii.y;
  const xAxisRotation = ellipse.xRotation.degrees;
  const largeArcFlag = ellipse.largeArc ? 1 : 0;
  const sweepFlag = ellipse.sweep ? 1 : 0;
  const endX = ellipse.end.x;
  const endY = ellipse.end.y;

  let components: (string | number)[] = [];
  if (includeMove) {
    components = ['M', startX, startY];
  }
  components.push('A', rx, ry, xAxisRotation, largeArcFlag, sweepFlag, endX, endY);
  return composeSvgPathData(components, fractionDigits);
}

/**
 * Parses SVG path data to extract the parameters of an elliptical arc.
 *
 * The function expects the path data to contain a move command ('M') followed by an arc command ('A').
 * It extracts the start point, radii, rotation, and flags for the arc, and the end point.
 *
 * @param pathData - The SVG path data string containing the move and arc commands.
 * @returns An object containing the start point, end point, radii, rotation angle, large arc flag, and sweep flag.
 * @throws {Error} If the path data does not contain enough tokens or if the expected commands are not found.
 */
export function endpointsEllipseFromSVGPathData(pathData: string): EndpointsEllipse {
  const tokens = tokenizeSvgPathData(pathData);
  if (tokens.length < 11) {
    // 3 for the move command, 8 for the arc command
    throw new Error('Invalid SVG path data: Not enough tokens.');
  }
  let i = 0;
  const moveTo = tokens[i++];
  if ((moveTo as string) !== 'M') {
    throw new Error('Invalid SVG path data: Expected move command.');
  }
  const startX = tokens[i++] as number;
  const startY = tokens[i++] as number;
  const start = point(startX, startY);

  const arcTo = tokens[i++];
  if ((arcTo as string) !== 'A') {
    throw new Error('Invalid SVG path data: Expected arc command.');
  }
  const rx = tokens[i++] as number;
  const ry = tokens[i++] as number;
  const radii = point(rx, ry);
  const xRotation = degrees(tokens[i++] as number);
  const largeArc = (tokens[i++] as number) !== 0;
  const sweep = (tokens[i++] as number) !== 0;
  const endX = tokens[i++] as number;
  const endY = tokens[i++] as number;
  const end = point(endX, endY);

  return {
    start,
    end,
    radii,
    xRotation,
    largeArc,
    sweep
  };
}
